import { IDetailsRowProps } from '@fluentui/react';
import {
  detailsHeaderStylesBasic,
  DetailsListEditor,
  detailsListStylesBasic,
  EmptyMessage,
  highlightRowBorder,
  IColumnWithDataType,
  IDetailsListEditorProps,
  ListRow,
  listRowStylesThinWithCellDivider,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useState } from 'react';

import { NameValue } from '../../featurestore/models';
import {
  editNameValueListStylesDefault,
  IEditNameValueListClassNames,
  IEditNameValueListStyles,
} from './EditNameValueList.styles';

const getColumns = (showDataType?: boolean, editable?: boolean) => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'name',
      name: showDataType ? 'Feature Name' : 'Key',
      fieldName: 'name',
      minWidth: 150,
    },
  ];
  if (showDataType) {
    columns.push({
      key: 'dataType',
      name: 'Data Type',
      fieldName: 'dataType',
      minWidth: 150,
    });
  }
  columns.push({
    key: 'value',
    name: 'Value',
    fieldName: 'value',
    minWidth: 150,
    data: {
      listCellProps: { editable },
    },
  });
  return columns;
};

interface IEditNameValueListProps {
  editable?: boolean;
  nameValues?: NameValue[];
  showDataType?: boolean;
  onUpdate: (nameValues: NameValue[]) => void;
}

export const EditNameValueList = ({ editable, nameValues = [], showDataType, onUpdate }: IEditNameValueListProps) => {
  const [items, setItems] = useState<NameValue[]>(nameValues);
  const onEndEdit = useCallback(
    (changed?: boolean, value?: any, rowIndex?: number, fieldName?: string) => {
      if (changed && rowIndex !== undefined && fieldName) {
        (items[rowIndex] as any)[fieldName] = value;
        setItems([...items]);
        onUpdate(items.map(({ name, value, dataType }) => ({ name, value, dataType })));
      }
    },
    [items]
  );
  const featureListProps: IDetailsListEditorProps = {
    columns: getColumns(showDataType, editable),
    headerStyles: detailsHeaderStylesBasic,
    styles: detailsListStylesBasic,
    items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [], itemIndex } = props || {};
      return (
        <ListRow
          styles={listRowStylesThinWithCellDivider}
          className={!item.name ? 'is-empty-row' : undefined}
          hideHeader
          data={item}
          columns={columns}
          itemIndex={itemIndex}
          onMouseEnter={(e) => highlightRowBorder(e)}
          onMouseLeave={(e) => highlightRowBorder(e, false)}
        />
      );
    },
    onEndEdit,
  };
  const classNames = useClassNames<IEditNameValueListStyles, IEditNameValueListClassNames>(
    'EditNameValueList',
    editNameValueListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="edit-online-ingest-list">
      <div className={classNames.list} data-is-scrollable="true">
        {items.length === 0 ? (
          <EmptyMessage message="No Features Available" />
        ) : (
          <DetailsListEditor {...featureListProps} />
        )}
      </div>
    </div>
  );
};
